<i18n>
{
	"en": {
		"orders": "orders",
		"noOrders": "There are no orders on this trip yet.",
		"customerName": "Customer",
		"eta": "ETA/ATA",
		"etd": "ETD/ATD",
		"from": "From",
		"loadingMeters": "Loading",
		"numberOfPallets": "Pallets",
		"order": "Order",
		"status": "Status",
		"to": "To",
		"ediStatus": "EDI",
		"volume": "Volume",
		"weight": "Gr. Weight",
		"removeOrderAndMerge": "Remove",
		"editOrder": "Edit Order",
		"moveOrder": "Move Order to Different Trip",
		"ediScheduleOrder": "Schedule order for EDI",
		"ediUnScheduleOrder": "Unschedule order for EDI",
		"packageLabel": "Labels",
		"waybill": "Waybill",
		"removeOrder": "Remove, but keep stretch"
	},
	"no": {
		"orders": "oppdrag",
		"noOrders": "Det er ingen oppdrag på denne turen ennå.",
		"customerName": "Kunde",
		"eta": "ETA/ATA",
		"etd": "ETD/ATD",
		"from": "Fra",
		"loadingMeters": "Lastm.",
		"numberOfPallets": "Kolli",
		"order": "Oppdrag",
		"status": "Status",
		"to": "Til",
		"ediStatus": "EDI",
		"volume": "Vol",
		"weight": "Br. vekt",
		"removeOrderAndMerge": "Fjern",
		"editOrder": "Rediger oppdrag",
		"moveOrder": "Flytt oppdrag til annen tur",
		"ediScheduleOrder": "Planlegg ordre for EDI",
		"ediUnScheduleOrder": "Fjern planlagt EDI sending",
		"packageLabel": "Etiketter",
		"waybill": "Fraktbrev",
		"removeOrder": "Fjern og behold strekning"
	}
}
</i18n>
<template>
	<div class="cargo-information">
		<div class="cargo-information-header">
			<t-panel-header
				:text="`${ordersCount} ${$t('orders')}`"
				icon="orders"
				size="md"
			/>
			<t-metric-details
				:value="tripDetails.volume"
				:max="Number(getProperty(tripDetails.cargoCarrier, 'volume'))"
				:label="$t('volume')"
				decimals="1"
				unit="m3"
				size="small"
			/>
			<t-metric-details
				:value="tripDetails.grossWeight"
				:max="Number(getProperty(tripDetails.cargoCarrier, 'weight'))"
				:label="$t('weight')"
				unit="kg"
				size="small"
			/>
			<t-metric-details
				:value="tripDetails.loadingMeters"
				:max="Number(getProperty(tripDetails.cargoCarrier, 'loadingMeters'))"
				:label="$t('loadingMeters')"
				decimals="1"
				unit="lm"
				size="small"
			/>
		</div>

		<t-grid
			v-if="ordersCount"
			ref="grid"
			:columnDefinitions="columnDefinitions"
			:defaultColumnDefinition="defaultColumnDef"
			:rowHeight="50"
			:items="sortedOrders"
			:showFilters="false"
			:selectableMode="['row']"
			selectable
			singleSelect
			class="cargo-information-content"
			@rowDoubleClick="setEditOrder"
			@delete="onGridDelete"
		>
			<template v-slot:status="{ item }">
				<div class="icon-container">
					<t-timpex-icon
						:icon="getStatusIcon(item)"
						:color="getStatusColor(item)"
						size="md"
					/>
					<t-pill
						v-if="item.deviations"
						size="small"
						color="red"
						style="position: absolute; top: -25%; right: 0;"
						isSolid
					>!</t-pill>
				</div>
			</template>
			<template v-slot:ediStatus="{item}">
				<div v-if="!isUnscheduled(item)" class="icon-container">
					<div v-if="isScheduled(item)">
						<t-timpex-icon
							:icon="'time'"
							:type="'bp'"
							:color="'orange'"
						/></div>
					<div v-if="isSent(item)">
						<t-timpex-icon
							:icon="'tick'"
							:type="'bp'"
							:color="'green'"
						/></div>
				</div>
			</template>
			<template v-slot:despatchParty="{ item }">
				<div v-if="item.despatchParty" style="height: 16px;">{{ item.despatchParty.name }}</div>
				<div v-if="item.despatchParty" class="grid-cell-sub-line">{{ item.despatchParty.street }}, {{ item.despatchParty.postalCode }} {{ item.despatchParty.postalPlace }}</div>
			</template>
			<template v-slot:deliveryParty="{ item }">
				<div v-if="item.deliveryParty" style="height: 16px;">{{ item.deliveryParty.name }}</div>
				<div v-if="item.deliveryParty" class="grid-cell-sub-line">{{ item.deliveryParty.street }}, {{ item.deliveryParty.postalCode }} {{ item.deliveryParty.postalPlace }}</div>
			</template>
			<template v-slot:customer="{ item }">
				<div v-if="item.customer" style="height: 16px;">{{ item.customer.name }}</div>
				<div v-if="item.customer" class="grid-cell-sub-line">{{ item.customer.customerNo }}</div>
			</template>
			<template v-slot:contextMenu="{ item }">
				<t-menu>
					<t-menu-group>
						<t-menu-item
							icon="edit"
							@click="setEditOrder(item.id)"
						>
							<strong>{{ $t('editOrder') }}</strong>
						</t-menu-item>
						<t-menu-item
							icon="inheritance"
							@click="$emit('moveOrder', { order: item, trip: tripDetails })"
						>
							{{ $t('moveOrder') }}
						</t-menu-item>
						<t-menu-item
							v-if="!isScheduled(item)"
							:disabled="!vendorHasEdipart()"
							icon="edi"
							@click="$emit('scheduleOrderForEdi', { subOrderId: item.id, id: tripDetails.id, type: tripDetails.type.key })"
						>
							{{ $t('ediScheduleOrder') }}
						</t-menu-item>
						<t-menu-item
							v-if="orderInstructionScheduled(item)"
							icon="edi"
							@click="$emit('unscheduleOrderForEdi', { subOrderId: item.id, id: tripDetails.id, type: tripDetails.type.key })"
						>
							{{ $t('ediUnScheduleOrder') }}
						</t-menu-item>
						<hr>
						<t-menu-item
							icon="cross"
							@click="$emit('removeOrderAndMerge', item)"
						>
							{{ $t('removeOrderAndMerge') }}
						</t-menu-item>
						<t-menu-item
							icon="cross"
							@click="$emit('removeOrder', item)"
						>
							{{ $t('removeOrder') }}
						</t-menu-item>
						<hr>
						<t-menu-item
							icon="package-label"
							@click="$emit('packageLabel', item.orderId)"
						>
							{{ $t('packageLabel') }}
						</t-menu-item>
						<t-menu-item
							icon="waybill"
							@click="$emit('waybill', item.orderId)"
						>
							{{ $t('waybill') }}
						</t-menu-item>
					</t-menu-group>
				</t-menu>
			</template>
		</t-grid>

		<t-no-content-panel
			v-else
			:text="$t('noOrders')"
			icon="order"
			class="cargo-information-content"
			noBetweenGap
		/>
	</div>
</template>

<script>
import {
	displayDateFormat, storageDateFormat, displayDateFormatter, dateExists, goodsTypesIcons, statuses, eventBus
} from '@/core';
import moment from 'moment';
import TMetricDetails from '@/components/evergreen/timpex/layers/TMetricDetails';
export default {
	name: 'CargoInformation',
	components: { TMetricDetails },
	props: { tripDetails: Object },
	data() {
		return {
			columnDefinitions: [],
			defaultColumnDef: { resizable: true },
			movingSubOrder: null
		};
	},
	computed: {
		ordersCount() {
			return this.tripDetails.orders ? this.tripDetails.orders.length : 0;
		},
		sortedOrders() {
			let result = [];
			if (this.tripDetails.orders) {
				result = [...this.tripDetails.orders];
				result.sort((a,b) => {
					let aNum = isNaN(a.loadingSeqNo) ? Number.MAX_SAFE_INTEGER : a.loadingSeqNo;
					let bNum = isNaN(b.loadingSeqNo) ? Number.MAX_SAFE_INTEGER : b.loadingSeqNo;
					return aNum - bNum;
				});
			}
			return result;
		}
	},
	beforeMount() {
		this.columnDefinitions = [
			{
				field: 'status',
				displayName: this.$t('status'),
				valueGetter: (item) => item.name,
				width: 50,
				center: true,
			},
			{
				field: 'orderNumber',
				displayName: this.$t('order'),
				valueGetter: (item) => item.orderNumber,
				resizable: false,
				width: 75,
				type: 'number',
				comparableValueGetter: (item) => Number(item.orderNumber),
			},
			{
				field: 'etd.date',
				displayName: this.$t('etd'),
				valueGetter: (item) => displayDateFormatter(this.dateGetter(item.atd, item.etd)),
				width: 75,
				resizable: false,
				type: 'date',
				dateFormat: displayDateFormat,
				comparableValueGetter: (item) => moment(this.dateGetter(item.atd, item.etd), storageDateFormat).startOf('day').toISOString(),
				cellClass(item) { return dateExists(item.atd) ? 'grid-cell-bold' : 'grid-cell-light'; }
			},
			{
				field: 'despatchParty',
				displayName: this.$t('from'),
				comparableValueGetter: (item) => this.addressGetter(item.despatchParty),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'deliveryParty',
				displayName: this.$t('to'),
				comparableValueGetter: (item) => this.addressGetter(item.deliveryParty),
				type: 'address',
				minWidth: 125,
			},
			{
				field: 'ediStatus',
				displayName: this.$t('ediStatus'),
				valueGetter: (item) => item.ediStatus,
				width: 100,
				type: 'edi',
			},
			{
				field: 'customer',
				displayName: this.$t('customerName'),
				comparableValueGetter: ({ customer }) => customer ? `${customer.name} ${customer.customerNo}` : '',
				minWidth: 125,
				type: 'customer',
			},
		];
	},
	methods: {
		getEdiIcon(item) {
			let transmission = item.edi.out_edipartId;
			if (transmission && item.edi.outboundSchedule.isSent)
				return { icon: 'tick', color: 'green' };
			else
				return { icon: 'time', color: 'orange' };
		},
		getProperty(object, propKey) {
			if (!object) return undefined;
			return object[propKey];
		},
		getStatusIcon(item) {
			return goodsTypesIcons(item.goodsType);
		},
		getStatusColor(item) {
			return statuses.color(item.status);
		},
		addressGetter(party) {
			if (!party) return '';
			return `${party.name} ${party.street} ${party.postalCode} ${party.postalPlace}`;
		},
		orderInstructionSent(item) {
			if (!item.edi)
				return false;
			return this.hasInstruction(item);
		},
		orderInstructionScheduled(item) {
			if (!item.edi)
				return false;
			return this.isScheduled(item);
		},
		isSent(item) {
			if (!item.edi)
				return false;
			if (!item.edi.outboundSchedule)
				return false;
			let { isScheduled, isSent } = item.edi.outboundSchedule;
			return !isScheduled && isSent;
		},
		isUnscheduled(item) {
			if (!item.edi)
				return false;
			if (!item.edi.outboundSchedule)
				return false;
			let { isScheduled, isSent } = item.edi.outboundSchedule;
			return !isScheduled && !isSent;
		},
		isScheduled(item) {
			if (!item.edi)
				return false;
			if (!item.edi.outboundSchedule)
				return false;
			let { isScheduled, isSent } =item.edi.outboundSchedule;
			return isScheduled && !isSent;
		},
		hasInstruction(item) {
			return Boolean(item.edi.out_edipartId);
		},
		vendorHasEdipart() {
			return Boolean(this.tripDetails.vendor.edipartId);
		},
		dateGetter(actual, estimated) {
			if (dateExists(actual)) return actual.date;
			if (dateExists(estimated)) return estimated.date;
			return undefined;
		},
		onGridDelete() {
			this.$emit('delete', this.$refs.grid.getSelectedItems());
		},
		setEditOrder(id) {
			let order = this.tripDetails.orders.find(x => x.id === id);
			if (!order) return;
			// TODO Cleanup, setEditOrder seem to be unused from newTripOrders.vue
			eventBus.$emit('setEditOrder', order.orderId);
		}
	}
};
</script>

<style lang="scss" scoped>
.cargo-information {
	height: 100%;
	min-height: 160px;
}

.cargo-information-header {
	display:grid;
	padding: 15px;
	grid-template-columns: auto repeat(3, max-content);
	grid-column-gap: 20px;
	background: white;
}

.cargo-information-content {
	height: calc(100% - 54px);
	min-height: inherit;
	width: 100%;
}

.icon-container {
	position: relative;
	width: 100%;
	display: flex;
	justify-content: center;
}
</style>
